import { useId } from "react";
import type { HourlyPeriod, TemperatureUnit } from "../../../shared/models";
import { convertTemp } from "../../../shared/weather";
import { EmptyState } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { WeatherIcon } from "../../ui/WeatherIcon";
import type { WidgetDefinition, WidgetProps } from "../types";
import { useWeatherSummary } from "../weather/WeatherWidget";
import { WidgetHeader } from "../WidgetHeader";
import { NeedsLocation, remPx, SkyFoot, useBoxSize, useTimeFmt } from "./common";

interface HourlyGraphConfig {
  hours: number;
  showIcons: boolean;
}

const HOUR = 3_600_000;

/** Smooth path through points (Catmull-Rom → cubic Bézier, gentle tension; never overshoots far). */
export function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M${pts[0]!.x} ${pts[0]!.y}`;
  const f = (n: number) => n.toFixed(1);
  let d = `M${f(pts[0]!.x)} ${f(pts[0]!.y)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? p2;
    const t = 0.16;
    d += ` C${f(p1.x + (p2.x - p0.x) * t)} ${f(p1.y + (p2.y - p0.y) * t)} ${f(p2.x - (p3.x - p1.x) * t)} ${f(p2.y - (p3.y - p1.y) * t)} ${f(p2.x)} ${f(p2.y)}`;
  }
  return d;
}

function HourlyGraph({ config, size, fullscreen, openFullscreen }: WidgetProps<HourlyGraphConfig>) {
  const { q, state, hasLocation, settings } = useWeatherSummary();
  const now = useNow("minute");
  const { h24 } = useTimeFmt();
  if (!hasLocation) return <NeedsLocation icon="thermo" title="Forecast needs a home location" detail="Set a general location to see the hourly forecast." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="cloud" title="Forecast unavailable" detail={q.error?.message} />;
  const unit = settings.temperatureUnit;
  const hours = data.hourly.filter((h) => Date.parse(h.startTime) + HOUR > now.getTime()).slice(0, fullscreen ? Math.max(config.hours, 36) : config.hours);
  if (hours.length < 2) return <EmptyState icon="cloud" title="No hourly forecast right now" />;

  const temps = hours.map((h) => convertTemp(h.temperature, h.temperatureUnit, unit));
  const hi = Math.max(...temps);
  const lo = Math.min(...temps);
  const hiAt = hours[temps.indexOf(hi)]!;
  const pops = hours.map((h) => h.precipChancePct ?? 0);
  const popMax = Math.max(...pops);
  const popAt = hours[pops.indexOf(popMax)]!;
  const fmtH = (iso: string) =>
    new Intl.DateTimeFormat("en-US", h24 ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: data.location.timeZone } : { hour: "numeric", timeZone: data.location.timeZone }).format(new Date(iso));
  const fc = data.stamps.forecast;

  return (
    <div className="sky sky-hourly">
      <WidgetHeader
        title={`Next ${hours.length} hours`}
        onTitle={fullscreen ? undefined : openFullscreen}
        meta={size.variant === "compact" ? undefined : `High ${Math.round(hi)}° at ${fmtH(hiAt.startTime)}${popMax >= 20 ? ` · ${popMax}% rain at ${fmtH(popAt.startTime)}` : ""}`}
      />
      <Graph hours={hours} temps={temps} lo={lo} hi={hi} unit={unit} showIcons={config.showIcons && size.height > remPx() * 15.8} />
      {size.variant !== "compact" && <SkyFoot source={`NWS hourly forecast${fc ? ` · issued ${relativeAge(fc.fetchedAt)}` : ""}`} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

function Graph({ hours, temps, lo, hi, unit, showIcons }: { hours: HourlyPeriod[]; temps: number[]; lo: number; hi: number; unit: TemperatureUnit; showIcons: boolean }) {
  const [ref, box] = useBoxSize<HTMLDivElement>();
  const fmt = useTimeFmt();
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const W = Math.max(160, box.width);
  const H = Math.max(110, box.height);
  const padX = 18;
  const iconH = showIcons ? 30 : 0;
  const labelTop = 22 + iconH; // temperature labels + icons above the line
  const axisH = 22; // hour labels
  const barsH = Math.max(26, Math.round((H - labelTop - axisH) * 0.26));
  const lineTop = labelTop + 6;
  const lineBottom = H - axisH - barsH - 10;
  const span = Math.max(4, hi - lo);
  const step = (W - 2 * padX) / (hours.length - 1);
  const x = (i: number) => padX + i * step;
  const y = (t: number) => lineBottom - ((t - lo) / span) * (lineBottom - lineTop);
  const pts = temps.map((t, i) => ({ x: x(i), y: y(t) }));
  const line = smoothPath(pts);
  const area = `${line} L${x(hours.length - 1).toFixed(1)} ${lineBottom + 8} L${x(0).toFixed(1)} ${lineBottom + 8} Z`;
  // Label every k-th hour so labels stay ≥ ~52px apart.
  const every = Math.max(1, Math.ceil(52 / step));
  const barW = Math.max(3, Math.min(18, step * 0.62));
  const barBase = H - axisH;
  const pops = hours.map((h) => h.precipChancePct ?? 0);
  const wettest = pops.indexOf(Math.max(...pops));
  const hiIdx = temps.indexOf(hi);
  const loIdx = temps.indexOf(lo);
  return (
    <div className="hourly-graph" ref={ref}>
      {box.width > 0 && (
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={`Temperatures from ${Math.round(lo)}° to ${Math.round(hi)}°${unit}`}>
          <defs>
            <linearGradient id={`${uid}-temp`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--accent)" stopOpacity="0.32" />
              <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Precipitation chance */}
          <line x1={padX - barW / 2} x2={W - padX + barW / 2} y1={barBase} y2={barBase} className="hg-base" />
          {hours.map((h, i) => {
            const p = h.precipChancePct ?? 0;
            if (p <= 0) return null;
            const bh = (p / 100) * barsH;
            return <rect key={`p${h.startTime}`} x={x(i) - barW / 2} y={barBase - bh} width={barW} height={bh} rx="2" className="hg-pop" style={{ opacity: 0.35 + (p / 100) * 0.65 }} />;
          })}
          {/* Temperature */}
          <path d={area} fill={`url(#${uid}-temp)`} />
          <path d={line} className="hg-line" />
          {hours.map((h, i) => {
            const labelled = i % every === 0;
            const extreme = i === hiIdx || i === loIdx;
            const pop = h.precipChancePct ?? 0;
            // Label rain chance at labelled hours, plus the wettest hour when it sits clear of them.
            const gap = Math.min(i % every, every - (i % every));
            const popLabel = pop >= 20 && (labelled || (i === wettest && gap * step >= 30));
            if (!labelled && !extreme && !popLabel) return null;
            const p = pts[i]!;
            return (
              <g key={h.startTime}>
                {(labelled || extreme) && <circle cx={p.x} cy={p.y} r={extreme ? 4 : 2.6} className={extreme ? "hg-dot hg-extreme" : "hg-dot"} />}
                {popLabel && (
                  <text x={p.x} y={barBase - (pop / 100) * barsH - 4} className="hg-pop-label">
                    {pop}%
                  </text>
                )}
                {labelled && (
                  <>
                    <text x={p.x} y={p.y - 10} className="hg-temp">
                      {Math.round(temps[i]!)}°
                    </text>
                    {showIcons && (
                      <g transform={`translate(${(p.x - 13).toFixed(1)} 0)`}>
                        <WeatherIcon kind={h.icon} isDaytime={h.isDaytime} size={26} />
                      </g>
                    )}
                    <text x={p.x} y={H - 5} className="hg-hour">
                      {i === 0 ? "Now" : fmt.hour(new Date(h.startTime))}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

export const hourlyGraphWidget: WidgetDefinition<HourlyGraphConfig> = {
  type: "hourly-graph",
  version: 1,
  name: "Hourly forecast graph",
  description: "Temperature line and chance of rain for the next day, from the NWS hourly forecast.",
  icon: "thermo",
  category: "weather",
  defaultSize: { w: 6, h: 3 },
  minSize: { w: 3, h: 2 },
  stackHeight: 280,
  defaults: { hours: 24, showIcons: true },
  settings: [
    {
      key: "hours",
      label: "Hours shown",
      type: "select",
      options: [
        { value: 12, label: "12 hours" },
        { value: 24, label: "24 hours" },
        { value: 36, label: "36 hours" },
        { value: 48, label: "48 hours" },
      ],
    },
    { key: "showIcons", label: "Show weather icons", type: "toggle" },
  ],
  provider: "nws",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: HourlyGraph,
};
